import {
  FormControl,
  FormLabel,
  Autocomplete,
  TextField,
  Stack,
  Tooltip,
} from '@mui/material';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

export const GcpNetworkSelect = ({
  accountId,
  network,
  onChange,
  disabledItems,
  title = 'Network',
  allowOnlyPeeredNetworks = false,
}: {
  accountId?: string;
  network?: GcpNetwork;
  onChange: (network: GcpNetwork) => void;
  disabledItems: { networkName: string; project: string }[];
  title?: string;
  allowOnlyPeeredNetworks?: boolean;
}) => {
  if (!accountId) {
    return <EmptySelect title={title} />;
  }

  return (
    <InnerGcpNetworkSelect
      accountId={accountId}
      network={network}
      onChange={onChange}
      disabledItems={disabledItems}
      title={title}
      allowOnlyPeeredNetworks={allowOnlyPeeredNetworks}
    />
  );
};

interface NetworkOption extends GcpNetwork {
  id: string;
}

export interface GcpNetwork {
  networkName: string;
  project: string;
  isPeered: boolean;
  isShared: boolean;
}

const InnerGcpNetworkSelect = ({
  accountId,
  network,
  onChange,
  disabledItems,
  title,
  allowOnlyPeeredNetworks,
}: {
  accountId: string;
  network?: GcpNetwork;
  onChange: (network: GcpNetwork) => void;
  disabledItems: { networkName: string; project: string }[];
  title: string;
  allowOnlyPeeredNetworks?: boolean;
}) => {
  const dal = useDAL();
  const { data: ownNetworks } = dal.restore.gcp.useListNetworks(accountId);
  const { data: sharedNetworks } =
    dal.restore.gcp.useListSharedNetworks(accountId);

  if (!ownNetworks?.networks?.length && !sharedNetworks?.networks?.length) {
    return <EmptySelect title={title} />;
  }

  const OwnOptionsMap: NetworkOption[] = [];
  const SharedOptionsMap: NetworkOption[] = [];

  (ownNetworks?.networks ?? []).forEach((n) => {
    OwnOptionsMap.push({
      id: `${n.networkName}-${n.project}`,
      networkName: n.networkName,
      isShared: false,
      project: n.project,
      isPeered: n.isPeered,
    });
  });

  (sharedNetworks?.networks ?? []).forEach((n) => {
    SharedOptionsMap.push({
      id: `${n.networkName}-${n.project}`,
      networkName: n.networkName,
      isShared: true,
      project: n.project,
      isPeered: n.isPeered,
    });
  });

  const options = [
    ...OwnOptionsMap.sort((a, b) => a.networkName.localeCompare(b.networkName)),
    ...SharedOptionsMap.sort((a, b) =>
      a.networkName.localeCompare(b.networkName)
    ),
  ];

  return (
    <FormControl size='small' className='flex-1'>
      <Stack
        className='flex-1 pr-[8px]'
        direction='row'
        alignItems='top'
        justifyContent='space-between'
      >
        <FormLabel>{title}</FormLabel>
        {allowOnlyPeeredNetworks && (
          <Tooltip
            title={
              'When using a private IP, only peered networks are available.'
            }
            slotProps={{
              tooltip: {
                sx: {
                  padding: '16px',
                },
              },
            }}
          >
            <i className='material-symbols-help-outline-rounded h-[20px] w-[20px] p-[2px] text-black' />
          </Tooltip>
        )}
      </Stack>
      <Autocomplete
        clearOnBlur
        size='small'
        options={options}
        value={
          options.find(
            (o) =>
              o.networkName === network?.networkName &&
              o.project === network?.project
          ) || null
        }
        onChange={(_, newValue) => {
          if (newValue) {
            onChange(newValue);
          }
        }}
        getOptionLabel={(option) => getNetworkLabel(option)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionDisabled={(option) =>
          disabledItems.some(
            (d) =>
              d.networkName === option.networkName &&
              d.project === option.project
          ) ||
          (!!allowOnlyPeeredNetworks && !option.isPeered)
        }
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              {getNetworkLabel(option)}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder='Select'
            label=''
            InputLabelProps={{ shrink: true }}
          />
        )}
      />
    </FormControl>
  );
};

export const getNetworkLabel = (network: Omit<GcpNetwork, 'isPeered'>) => {
  return network.isShared && network.project
    ? `${network.networkName} (Shared from ${network.project})`
    : network.networkName;
};
